import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Award, Plus, Search, Trash2, X } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api/client'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Skeleton } from '../../components/ui/Skeleton'
import toast from 'react-hot-toast'

export default function BrandsPage() {
  const [search, setSearch] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [form, setForm] = useState({
    name: '',
    slug: ''
  })

  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['/products/brands', { search }],
    queryFn: () => api.get('/products/brands', { params: { search } }).then(r => r.data),
  })

  const createMutation = useMutation({
    mutationFn: (newBrand: typeof form) => api.post('/products/brands', newBrand),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/products/brands'] })
      toast.success('برند جدید اضافه شد')
      setIsOpen(false)
      setForm({ name: '', slug: '' })
    },
    onError: () => toast.error('خطا در ایجاد برند')
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/products/brands/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/products/brands'] })
      toast.success('برند حذف شد')
    },
    onError: () => toast.error('خطا در حذف برند')
  })

  const items = Array.isArray(data) ? data : (data?.items || [])

  return (
    <div className="p-4 space-y-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-neutral-900 dark:text-white">برندها و برندینگ</h1>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">مدیریت تولیدکنندگان و برندهای تجاری کالاهایتان</p>
        </div>
        <Button onClick={() => setIsOpen(true)}>
          <Plus className="w-4 h-4 ml-1" />
          برند جدید
        </Button>
      </motion.div>

      <div className="relative max-w-md">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
        <Input placeholder="جستجوی برند..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
      </div>

      {isLoading ? (
        <div className="space-y-3">{[1, 2, 3].map(i => <div key={i} className="card p-4"><Skeleton className="h-5 w-48" /></div>)}</div>
      ) : items.length === 0 ? (
        <div className="card p-12 text-center">
          <Award className="w-12 h-12 mx-auto text-neutral-300 dark:text-neutral-600 mb-3" />
          <p className="text-neutral-500 dark:text-neutral-400">برندی تعریف نشده است</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {items.map((item: any) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="card p-4 flex items-center justify-between hover:shadow-md transition-shadow"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 flex items-center justify-center font-bold">
                  <Award className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-neutral-900 dark:text-white">{item.name}</h3>
                  <p className="text-xs text-neutral-500 font-mono">{item.slug || 'brand-slug'}</p>
                </div>
              </div>
              <button
                onClick={() => deleteMutation.mutate(item.id)}
                className="text-red-500 hover:text-red-700 p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/20"
                title="حذف"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </motion.div>
          ))}
        </div>
      )}

      {/* Modal create brand */}
      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white dark:bg-neutral-900 rounded-2xl p-6 w-full max-w-md shadow-xl border border-neutral-200 dark:border-neutral-800 space-y-4"
            >
              <div className="flex items-center justify-between pb-3 border-b border-neutral-100 dark:border-neutral-800">
                <h3 className="text-lg font-bold text-neutral-900 dark:text-white flex items-center gap-2">
                  <Award className="w-5 h-5 text-amber-500" />
                  افزودن برند جدید
                </h3>
                <button onClick={() => setIsOpen(false)} className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  if (!form.name) {
                    toast.error('نام برند الزامی است')
                    return
                  }
                  createMutation.mutate(form)
                }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1">نام برند (فارسی / انگلیسی)</label>
                  <Input
                    placeholder="مثلا: سامسونگ یا Samsung"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1">شناسه انگلیسی (Slug)</label>
                  <Input
                    placeholder="samsung"
                    value={form.slug}
                    onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  />
                </div>

                <div className="flex items-center justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
                    انصراف
                  </Button>
                  <Button type="submit" isLoading={createMutation.isPending}>
                    ذخیره برند
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  )
}
